/**
 * Pre-image stash (Phase E — loud rejection support).
 *
 * Every optimistic local write fires off a wire push with a
 * `mutationId`. The server either accepts (op-ack), rejects
 * (op-rejected), or never replies (timeout). For accept the local
 * write IS the final state and no revert is needed. For reject /
 * partial-reject / timeout the client has to roll the local state
 * back to what it was before the optimistic write — otherwise a
 * rejected edit silently lingers in the UI as if it had landed.
 *
 * The stash pairs each in-flight `mutationId` with a `revert()`
 * thunk that restores the relevant store slice to its pre-image. The
 * thunk is opaque to the stash — callers know what they captured and
 * how to put it back (one map entry's worth of state for a scoped
 * write, a flag flip for a settings toggle, etc.).
 *
 * Flow:
 *   1. Mutation fires → caller snapshots pre-image + builds revert thunk.
 *   2. Caller registers `(mutationId, revert)` with the stash.
 *   3. `op-ack` for that mutationId → `drop(mutationId)`. Revert discarded.
 *   4. `op-rejected` / partial / timeout → `consume(mutationId)?.revert()`.
 *
 * No React, no Zustand, no DOM. The revert closures own whatever
 * store/storage handles they need.
 */

export type StashedPreImage = {
  mutationId: string;
  revert: () => void;
};

export type PreImageStash = {
  /** Register a revert thunk for an in-flight mutation. */
  register(mutationId: string, revert: () => void): void;
  /** Consume + remove the revert thunk. Returns it for the caller to invoke. */
  consume(mutationId: string): StashedPreImage | undefined;
  /** Drop the revert thunk without invoking it (on successful ack). */
  drop(mutationId: string): void;
  /** Drop everything. Used on signout. */
  clear(): void;
  /** Test-only: snapshot the current set of pending mutationIds. */
  _snapshot(): string[];
};

export function createPreImageStash(): PreImageStash {
  const stash = new Map<string, StashedPreImage>();
  return {
    register(mutationId, revert): void {
      stash.set(mutationId, { mutationId, revert });
    },
    consume(mutationId): StashedPreImage | undefined {
      const entry = stash.get(mutationId);
      stash.delete(mutationId);
      return entry;
    },
    drop(mutationId): void {
      stash.delete(mutationId);
    },
    clear(): void {
      stash.clear();
    },
    _snapshot(): string[] {
      return [...stash.keys()];
    },
  };
}

// ---------------------------------------------------------------------------
// Process-wide singleton — mirrors `pending-mutations-singleton.ts`.
// ---------------------------------------------------------------------------

let stashSingleton: PreImageStash | null = null;

export function getPreImageStash(): PreImageStash {
  if (!stashSingleton) stashSingleton = createPreImageStash();
  return stashSingleton;
}

/** Test-only: replace the singleton (e.g. for deterministic state). */
export function _setPreImageStash(next: PreImageStash): void {
  stashSingleton = next;
}

/** Test-only: reset the singleton. */
export function _resetPreImageStash(): void {
  stashSingleton?.clear();
  stashSingleton = null;
}
